<template>
  <section
    class="card"
    :class="{ desktop: $isDesktop }"
    :data-campsite-slug="slug"
  >
    <section class="card-img">
      <img
        v-if="primaryPhotoUrl"
        loading="lazy"
        :src="primaryPhotoUrl.url.small"
        :srcset="`${primaryPhotoUrl.url.small} 300w, ${primaryPhotoUrl.url.large} 480w`"
        :alt="photoCaptionAltFormatter(primaryPhotoUrl.caption)"
        sizes="60vw"
      />
    </section>

    <div class="card-content">
      <mini-card-distance v-if="distance" :distance="distance" />

      <pu-rating-bubble
        :rating="rating"
        :rate-count="rateCount"
        class="cs-rating"
      />

      <h4 class="card-name">
        {{ name }}
      </h4>
      <p class="location">
        {{ hierarchyStringShort }}
      </p>
    </div>

    <div class="card-footer">
      <span v-if="leadPrice" class="lead-price">
        <slot name="price">
          <campsite-map-card-price
            :price="leadPrice"
            :lead-price-adults="leadPriceAdults"
            :lead-price-nights="leadPriceNights"
          />
        </slot>
      </span>
      <span v-else-if="price" class="lead-price">
        <slot name="price">
          <mini-card-price :price="price" />
        </slot>
      </span>
      <pu-category-icons :categories="categories" class="category-icons" />
    </div>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
import { SimpleMoney } from '~/utility/money'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import {
  MiniCardPrimaryPhoto,
  MiniCardProps,
  MiniCardPropsCategoryIcon,
} from './MiniCardProps'

@Component({})
class MiniCard extends Vue implements MiniCardProps {
  @Prop({ required: true })
  slug: string

  @Prop({ required: true })
  name: string

  @Prop()
  categories: MiniCardPropsCategoryIcon[]

  @Prop()
  primaryPhotoUrl: MiniCardPrimaryPhoto

  @Prop()
  rating: number

  @Prop()
  rateCount: number

  @Prop()
  hierarchyStringShort: string

  @Prop()
  distance?: { km: number; mi: number }

  @Prop()
  price: SimpleMoney

  @Prop({ required: false })
  leadPrice?: SimpleMoney

  @Prop({ required: false })
  leadPriceAdults?: number

  @Prop({ required: false })
  leadPriceNights?: number

  get head() {
    return {
      script: [this.imageObject],
    }
  }

  get imageObject() {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      name: this.name,
      contentLocation: `${this.name}, ${this.hierarchyStringShort}`,
    }
    if (this.primaryPhotoUrl && this.primaryPhotoUrl.url.large) {
      json.contentUrl = `https://${this.$config.public.envUrl}${this.primaryPhotoUrl.url.large}`
    }
    return {
      type: 'application/ld+json',
      body: true,
      json,
    }
  }

  photoCaptionAltFormatter(caption) {
    return photoCaptionAltFormatter(caption)
  }
}

export default toNative(MiniCard)
</script>

<style scoped>
.card {
  align-self: flex-start;
  background-color: white;
  border-radius: 0.5em;
  box-shadow: 0 0.33em var(--colour-dark-green);
  display: flex;
  filter: drop-shadow(-5px 15px 15px rgba(0, 0, 0, 0.5));
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  text-align: center;
}

.card-img {
  background: #fff url('~/assets/pitchup.com-vector-logo.svg') no-repeat center
    center;
  background-size: 40%;
  border-radius: 0.5em 0.5em 0 0;
  display: block;
  height: auto;
  min-height: 43vw;
  overflow: hidden;
  width: 100%;
}

.card-content,
.card-footer {
  text-align: center;
  flex-direction: column;
  position: relative;
}

.card-content {
  padding: 1.25em 0.5em 0;
  margin: 0.75em 0 0.25em;
  width: 100%;
  flex-grow: 1;
  display: flex;
  justify-content: space-evenly;
}

.card-footer {
  padding: 0 0 0.6em 0;
  margin: 0.4em 0 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-name {
  line-height: 1.2;
  font-size: 1em;
  margin: 0;
  justify-self: start;
  overflow-wrap: break-word;
}

.card .cs-rating {
  position: absolute;
  top: -0.5em;
  right: 0.8em;
}

.card .cs-rating:before {
  content: '';
}

.location {
  font-size: 0.8em;
  color: var(--colour-text-light);
  margin-top: 0.5em;
}

.lead-price {
  font-size: 0.8em;
  color: var(--colour-text-light);
  display: inline-block;
  margin: 0 0 0.5em 0;
}

.lead-price :deep(em),
.lead-price :deep(.display-price) {
  color: var(--colour-header-light-blue);
  font-weight: bold;
}

/* desktop & tablet */
.desktop .card-img {
  min-height: auto;
  aspect-ratio: 4/3;
}

.desktop .card {
  transition: box-shadow 0.15s ease-out;
  filter: drop-shadow(-5px 9px 9px rgba(0, 0, 0, 0.3));
}

.desktop a.mini-card-link.active .card,
.desktop a.mini-card-link:hover .card,
.desktop a.mini-card-link:focus-visible .card {
  box-shadow: 0 0 0 0.33em black;
}

.desktop a.mini-card-link:focus-visible {
  outline: none;
}
</style>
